// @ts-strict-ignore
import {Injectable} from "@angular/core";
import {
	CallingApplication,
	CanRecordResponse,
	CanRecordResult,
	MasterOfCeremonyMapping,
	PlayerMapping,
	RecorderMapping,
	RecorderType,
	RegisterResponse,
	RegisterResult,
	TabletMapping,
	ViewingStationMapping,
} from "@models";
import {ClientRegisterType} from "../../../modules/dialog/material-dialogs/general-dialogs/client-register-dialog/client-register-dialog.component";
import {environment} from "../../../../environments/environment";
import {Router} from "@angular/router";
import {
	DialogDataProRecordingFinishedDialog,
	DialogDataProRecordingFinishedDialogOutput,
} from "../../../modules/dialog/material-dialogs/recording-dialogs/pro-recording-finished-dialog/pro-recording-finished-dialog.component";
import {SimMaterialDialogService} from "../../../modules/dialog/sim-material-dialog.service";
import {SimSystemExtendedService} from "./sim-service-system.service";
import {SystemHub} from "../../../api/generated/hub";
import {Translations} from "@translations";
import {TranslateService} from "@ngx-translate/core";

export interface ClientRegisterResult<T> {
	mapping: T;
}

@Injectable({
	providedIn: "root",
})
export class SimClientRegisterService {
	constructor(
		private _simDialogService: SimMaterialDialogService,
		private _simSystemService: SimSystemExtendedService,
		private _systemHub: SystemHub,
		private _router: Router,
		private _translate: TranslateService,
	) {}

	public async registerPlayer(): Promise<ClientRegisterResult<PlayerMapping>> {
		let registerCmd = (initialRequest: boolean) =>
			this._simSystemService.registerPlayer({
				connectionId: this._systemHub.connectionId,
				isInitialRequest: initialRequest ?? false,
			});

		return this.RegisterInternal(registerCmd, "Player");
	}

	public async registerRecorder(
		callingApplication: CallingApplication,
		trainingSessionId: string,
	): Promise<ClientRegisterResult<RecorderMapping>> {
		let registerCmd = (initialRequest: boolean) =>
			this._simSystemService.registerRecorder({
				recorderType: RecorderType.ProRecorder,
				connectionId: this._systemHub.connectionId,
				reset: false,
				isInitialRequest: initialRequest ?? false,
				callingApplication: callingApplication,
			});

		let result = await this.RegisterInternal(registerCmd, "Recorder");
		if (!result?.mapping) {
			return result;
		}

		let canRecordResult = await this.canRecord(trainingSessionId);

		if (canRecordResult.result == CanRecordResult.Ok && !canRecordResult.mapping) {
			return await this.registerRecorder(callingApplication, trainingSessionId);
		}

		return canRecordResult;
	}

	public async handlePlayerTakeOver(playerIds: string[]) {
		return await this._simDialogService
			.openConfirmationDialog({
				title: this._translate.instant(Translations.CONFIRM_TAKE_OVER_DIALOG_TITLE),
				description: `${this._translate.instant(Translations.CONFIRM_TAKE_OVER_DIALOG_DESCRIPTION)}`,
				okText: this._translate.instant(Translations.BUTTON_OK),
				cancelText: this._translate.instant(Translations.BUTTON_CANCEL),
			})
			.then(async (result) => {
				if (result.action === "ok") {
					await this._simSystemService.takeAwayPlayers({
						playerIds: playerIds,
					});
					return true;
				}
				return false;
			});
	}

	private async canRecord(trainingSessionId: string) {
		let canRecordResult = await this._simSystemService.canRecord({
			trainingSessionId,
		});

		switch (canRecordResult.result) {
			case CanRecordResult.Ok:
				break;
			case CanRecordResult.Error:
			case CanRecordResult.TrainingSessionMissing:
				console.error(CanRecordResult[canRecordResult.result]);
				await this._router.navigate([environment.routes.eventDashboard]);
				break;
			case CanRecordResult.TrainingSessionNeedContinue:
				await this.openRecordingFinishedModal(canRecordResult);
				break;
			case CanRecordResult.RecorderHasOtherActiveRecording:
			case CanRecordResult.RecordingHasOtherActiveRecorder:
			case CanRecordResult.RecordingIsPreparedByOtherActiveRecorder:
				console.error(CanRecordResult[canRecordResult.result]);
				let dialogResult = await this._simDialogService.openCanRecordBlockedDialog({
					canRecord: canRecordResult,
				});
				canRecordResult.result = dialogResult.result;
				break;
		}
		return canRecordResult;
	}

	public async registerMoC(
		callingApplication: CallingApplication,
		scenarioRecordingId: string,
	): Promise<ClientRegisterResult<MasterOfCeremonyMapping>> {
		let registerCmd = (initialRequest: boolean) =>
			this._simSystemService.registerMoC({
				connectionId: this._systemHub.connectionId,
				scenarioRecordingId: scenarioRecordingId,
				isInitialRequest: initialRequest ?? false,
				callingApplication: callingApplication,
			});

		return this.RegisterInternal(registerCmd, "MasterOfCeremony");
	}

	public async registerTablet(
		callingApplication: CallingApplication,
		scenarioRecordingId: string,
	): Promise<ClientRegisterResult<TabletMapping>> {
		let registerCmd = (initialRequest: boolean) =>
			this._simSystemService.registerTablet({
				connectionId: this._systemHub.connectionId,
				scenarioRecordingId: scenarioRecordingId,
				isInitialRequest: initialRequest ?? false,
				callingApplication: callingApplication,
			});

		return this.RegisterInternal(registerCmd, "Tablet");
	}

	public async registerViewingStation(
		callingApplication: CallingApplication,
		scenarioRecordingId: string,
	): Promise<ClientRegisterResult<ViewingStationMapping>> {
		let registerCmd = (initialRequest: boolean) =>
			this._simSystemService.registerViewingStation({
				connectionId: this._systemHub.connectionId,
				scenarioRecordingId: scenarioRecordingId,
				isInitialRequest: initialRequest ?? false,
				callingApplication: callingApplication,
			});

		return this.RegisterInternal(registerCmd, "ViewingStation");
	}

	private async RegisterInternal<T>(
		registerCmd: (initialRequest: boolean) => Promise<RegisterResponse<T>>,
		type: ClientRegisterType,
	): Promise<ClientRegisterResult<T>> {
		let result = await registerCmd(true);
		if (result.result == RegisterResult.Success) {
			return result;
		} else {
			let dialogResult = await this._simDialogService.openClientRegisterDialog({
				cmd: registerCmd,
				initial: result,
				type: type,
			});
			return dialogResult.result;
		}
	}

	protected async openRecordingFinishedModal(canRecordResponse: CanRecordResponse) {
		const recData = canRecordResponse.additionalData.newRecording;
		const data: DialogDataProRecordingFinishedDialog = {
			width: 500,
			trainingSession: recData.teamTrainingSession,
			recorderMapping: canRecordResponse.mapping,
			stopRecordingFinishedEmitter: null,
			event: recData.event,
			scenarioRecordingId: recData.scenarioRecordingId,
			scenarioRecordingIds: [recData.scenarioRecordingId],
		};

		const dialogRef = this._simDialogService.openProRecordingFinishedDialog(data);

		let ret = await dialogRef.then(
			async (outputData: DialogDataProRecordingFinishedDialogOutput) => {
				if (outputData?.action === "ok") {
					switch (outputData.followUpAction) {
						case "archive":
							await this._router.navigate([`/web-manager/list/scenario-archive/player/0/${recData.scenarioRecordingId}`]);
							break;
						case "dashboard":
							await this._router.navigate([environment.routes.eventDashboard]);
							break;
						case "continue":
							return true;
						case "sessionSelection":
							await this._router.navigate([environment.routes.sessionSelection, environment.routes.proSessionSelection]);
							break;
					}
				}
			},
			async (error) => {
				await this._router.navigate([environment.routes.sessionDashboard]);
			},
		);
		return ret;
	}
}
